import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

const prompts = [
  "astronaut",
  "baking a cake",
  "penguin",
  "riding a bicycle",
  "superhero",
  "playing guitar",
  "rainstorm",
  "sleepwalking",
];
export const charadesStateSchema = z.object({
  players: z.array(z.string()),
  activeIndex: z.number().int().nonnegative(),
  prompt: z.string(),
  usedPrompts: z.array(z.string()),
  scores: z.record(z.string(), z.number()),
  phase: z.enum(["ready", "acting", "finished"]),
  round: z.number().int().nonnegative(),
  deadline: z.number().nullable(),
});
export const charadesEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start") }),
  z.object({ type: z.literal("correct") }),
  z.object({ type: z.literal("skip") }),
  z.object({ type: z.literal("finish") }),
]);
export const charadesSettingsSchema = z.object({
  roundSeconds: z.number().int().min(30).max(180).default(60),
  rounds: z.number().int().min(1).max(20).default(5),
  familySafe: z.boolean().default(true),
});
export type CharadesState = z.infer<typeof charadesStateSchema>;
export type CharadesEvent = z.infer<typeof charadesEventSchema>;
export type CharadesSettings = z.infer<typeof charadesSettingsSchema>;
const nextPrompt = (used: string[]) =>
  prompts.find((prompt) => !used.includes(prompt)) ??
  prompts[used.length % prompts.length] ??
  "penguin";

export const charadesGame: TopzleGameDefinition<
  CharadesState,
  CharadesEvent,
  CharadesSettings,
  { prompt: string | null }
> = {
  id: "charades",
  version: "1.0.0",
  name: "AI Charades",
  shortDescription: "Private prompts, quick rounds, teams, and a video-first stage.",
  longDescription:
    "Act safe curated prompts with optional locally generated variations after moderation.",
  category: "party",
  icon: "theater",
  cover: "charades",
  minPlayers: 2,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: true,
  supportsAiPlayers: false,
  supportsLocalAi: true,
  supportsCloudAi: true,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["video"],
  supportedLayouts: ["host_focused", "active_speaker", "grid"],
  preferredLayout: "host_focused",
  stateSchema: charadesStateSchema,
  eventSchema: charadesEventSchema,
  settingsSchema: charadesSettingsSchema,
  createInitialState(context) {
    const prompt = nextPrompt([]);
    return {
      players: context.playerIds,
      activeIndex: 0,
      prompt,
      usedPrompts: [prompt],
      scores: Object.fromEntries(context.playerIds.map((id) => [id, 0])),
      phase: "ready",
      round: 0,
      deadline: null,
    };
  },
  validateEvent(state, event, context) {
    const active = state.players[state.activeIndex];
    if (event.type === "start")
      return { valid: state.phase === "ready" && context.actorId === active };
    if (event.type === "finish") return { valid: context.actorId === active };
    return {
      valid: state.phase === "acting" && context.actorId === active,
      code: "not_active_player",
      message: "Only the active player can confirm the prompt",
    };
  },
  reduceEvent(state, event, context) {
    if (event.type === "start")
      return { ...state, phase: "acting", deadline: context.now + 60_000 };
    if (event.type === "finish") return { ...state, phase: "finished", deadline: null };
    const scores = { ...state.scores };
    if (event.type === "correct") {
      const active = state.players[state.activeIndex];
      if (active) scores[active] = (scores[active] ?? 0) + 1;
    }
    const prompt = nextPrompt(state.usedPrompts);
    return {
      ...state,
      prompt,
      usedPrompts: [...state.usedPrompts, prompt],
      scores,
      activeIndex: (state.activeIndex + 1) % state.players.length,
      round: state.round + 1,
      phase: "ready",
      deadline: null,
    };
  },
  getPublicState: (state, viewer) => ({
    ...state,
    prompt: viewer.viewerId === state.players[state.activeIndex] ? state.prompt : null,
  }),
  getPrivatePlayerState: (state, playerId) => ({
    prompt: playerId === state.players[state.activeIndex] ? state.prompt : null,
  }),
  getSpectatorState: (state) => ({ ...state, prompt: null }),
  getAvailableActions: (state, playerId) =>
    playerId === state.players[state.activeIndex]
      ? [{ type: state.phase === "ready" ? "start" : "correct" }, { type: "skip" }]
      : [],
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    const high = Math.max(...Object.values(state.scores));
    return {
      status: "completed",
      winnerIds: Object.entries(state.scores)
        .filter(([, score]) => score === high)
        .map(([id]) => id),
      scores: state.scores,
      reason: "rounds_complete",
    };
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 12 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return {
      state: {
        ...state,
        players: [...state.players, arrival.playerId],
        scores: { ...state.scores, [arrival.playerId]: 0 },
      },
    };
  },
  handlePlayerDeparture(state, departure) {
    const removedIndex = state.players.indexOf(departure.playerId);
    if (removedIndex < 0 || state.players.length <= 1) return null;
    const players = state.players.filter((id) => id !== departure.playerId);
    const removedActivePlayer = removedIndex === state.activeIndex;
    const scores = { ...state.scores };
    delete scores[departure.playerId];
    return {
      state: {
        ...state,
        players,
        scores,
        activeIndex: removedActivePlayer
          ? removedIndex % players.length
          : removedIndex < state.activeIndex
            ? state.activeIndex - 1
            : state.activeIndex,
        phase: removedActivePlayer && state.phase === "acting" ? "ready" : state.phase,
        deadline: removedActivePlayer ? null : state.deadline,
      },
    };
  },
};
